import { useState } from "react"
import { toast } from "sonner"

import { ApiError } from "@/api/client"
import {
  useCambiarRol,
  useCrearOperador,
  useDesactivarOperador,
  useOperadores,
  useYo,
} from "@/api/queries"
import type { OperadorCreado, Rol } from "@/api/types"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatearFecha } from "@/shared/format"
import { SecretoUnaVez } from "@/shared/secreto-una-vez"

const ROLES: { valor: Rol; etiqueta: string; explica: string }[] = [
  { valor: "viewer", etiqueta: "Solo lectura", explica: "Ve el consumo. No toca nada." },
  {
    valor: "admin",
    etiqueta: "Administrador",
    explica: "Aplicaciones, claves, catálogo y presupuestos.",
  },
  {
    valor: "owner",
    etiqueta: "Propietario",
    explica: "Además, credenciales y personas.",
  },
]

function etiquetaRol(rol: string): string {
  return ROLES.find((r) => r.valor === rol)?.etiqueta ?? rol
}

/**
 * Las personas con acceso a la consola. Solo la ve un `owner`.
 *
 * Dos cosas que la pantalla hace explícitas porque el backend las impone y sin
 * explicación parecen arbitrarias:
 *
 * - **Cambiar el rol o desactivar a alguien cierra sus sesiones al momento.** Es lo que
 *   hace que "expulsar" signifique algo, en vez de surtir efecto ocho horas después.
 * - **Nadie se toca a sí mismo.** Es el único camino para que una organización se quede
 *   sin ningún propietario, y de ahí no se sale desde la consola.
 */
export function OperatorsPage() {
  const operadores = useOperadores()
  const yo = useYo()
  const [creada, setCreada] = useState<OperadorCreado | null>(null)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Personas</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Quién puede entrar en esta consola, y con qué permisos.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          {operadores.isPending ? (
            <Skeleton className="h-32" />
          ) : operadores.isError ? (
            <p className="text-sm text-destructive">{mensajeDe(operadores.error)}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Correo</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Alta</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {operadores.data.map((o) => {
                  const soyYo = o.operator_id === yo.data?.operator_id
                  return (
                    <TableRow key={o.operator_id} className={o.activo ? "" : "opacity-60"}>
                      <TableCell className="font-medium">
                        {o.email}
                        {soyYo && (
                          <span className="ml-2 text-xs text-muted-foreground">(tú)</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {o.activo ? (
                          <SelectorRol operador={o.operator_id} rol={o.role} bloqueado={soyYo} />
                        ) : (
                          <Badge variant="outline">{etiquetaRol(o.role)}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatearFecha(o.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        {o.activo ? (
                          <Desactivar operador={o.operator_id} correo={o.email} soyYo={soyYo} />
                        ) : (
                          <Badge variant="destructive">desactivada</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Alta onCreada={setCreada} />

      <SecretoUnaVez
        abierto={creada !== null}
        titulo="Persona dada de alta"
        subtitulo={creada?.email}
        secreto={creada?.password ?? ""}
        tituloAviso="Cópiala ahora"
        aviso="Esta contraseña no se puede volver a consultar. Pásasela por un canal seguro y dile que la cambie al entrar."
        etiquetaCopiar="Copiar contraseña"
        etiquetaPendiente="Copia la contraseña para continuar"
        onCerrar={() => setCreada(null)}
      />
    </div>
  )
}

function mensajeDe(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return "Solo el propietario de la organización puede gestionar personas."
  }
  return "No se pudieron cargar las personas."
}

function SelectorRol({
  operador,
  rol,
  bloqueado,
}: {
  operador: string
  rol: Rol
  bloqueado: boolean
}) {
  const cambiar = useCambiarRol()

  if (bloqueado) {
    // No es una restricción de la consola: el gateway devuelve 409. Se enseña
    // deshabilitado en vez de esconderlo para que se vea cuál es tu propio rol.
    return (
      <Badge variant="secondary" title="No puedes cambiarte el rol a ti mismo">
        {etiquetaRol(rol)}
      </Badge>
    )
  }

  return (
    <select
      aria-label="Rol"
      value={rol}
      disabled={cambiar.isPending}
      className="h-8 rounded-md border bg-transparent px-2 text-sm"
      onChange={(e) => {
        cambiar.mutate(
          { operatorId: operador, role: e.target.value as Rol },
          {
            onSuccess: () => toast.success("Rol cambiado. Se han cerrado sus sesiones."),
            onError: () => toast.error("No se pudo cambiar el rol."),
          },
        )
      }}
    >
      {ROLES.map((r) => (
        <option key={r.valor} value={r.valor}>
          {r.etiqueta}
        </option>
      ))}
    </select>
  )
}

function Desactivar({
  operador,
  correo,
  soyYo,
}: {
  operador: string
  correo: string
  soyYo: boolean
}) {
  const desactivar = useDesactivarOperador()

  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={soyYo || desactivar.isPending}
      title={soyYo ? "No puedes desactivarte a ti mismo" : undefined}
      onClick={() => {
        desactivar.mutate(operador, {
          onSuccess: () => toast.success(`${correo} ya no puede entrar.`),
          onError: () => toast.error("No se pudo desactivar."),
        })
      }}
    >
      Desactivar
    </Button>
  )
}

function Alta({ onCreada }: { onCreada: (o: OperadorCreado) => void }) {
  const [email, setEmail] = useState("")
  const [role, setRole] = useState<Rol>("viewer")
  const crear = useCrearOperador()

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    try {
      const nueva = await crear.mutateAsync({ email: email.trim(), role })
      setEmail("")
      onCreada(nueva)
    } catch (e) {
      toast.error(
        e instanceof ApiError && e.status === 409
          ? "Ese correo ya está dado de alta."
          : "No se pudo dar de alta.",
      )
    }
  }

  return (
    <Card className="max-w-md">
      <CardHeader>
        <CardTitle>Dar de alta</CardTitle>
        <CardDescription>
          La contraseña la genera el sistema y se muestra una sola vez.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={enviar} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="correo">Correo</Label>
            <Input
              id="correo"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="persona@empresa.com"
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rol">Rol</Label>
            <select
              id="rol"
              value={role}
              onChange={(e) => setRole(e.target.value as Rol)}
              className="h-9 w-full rounded-md border bg-transparent px-3 text-sm"
            >
              {ROLES.map((r) => (
                <option key={r.valor} value={r.valor}>
                  {r.etiqueta}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              {ROLES.find((r) => r.valor === role)?.explica}
            </p>
          </div>
          <Button type="submit" disabled={crear.isPending || email.trim() === ""}>
            {crear.isPending ? "Dando de alta…" : "Dar de alta"}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
